import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import styles from './RootCauseAlarms.module.css';
import GoToDashboardButton from '../components/GoToDashboardButton';

const API_ORIGIN =
    process.env.REACT_APP_API_BASE ||
    (['localhost', '127.0.0.1'].includes(window.location.hostname)
        ? 'http://localhost:3001'
        : '');

const normalizedApiOrigin = API_ORIGIN.replace(/\/$/, '');
const API_BASE = normalizedApiOrigin.endsWith('/api')
    ? normalizedApiOrigin
    : `${normalizedApiOrigin}/api`;

const severityRank = { critical: 4, high: 3, medium: 2, low: 1 };

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const RootCauseAlarms = () => {
    const [alarms, setAlarms] = useState([]);
    const [summary, setSummary] = useState(null);
    const [selectedAlarm, setSelectedAlarm] = useState(null);
    const [sortBy, setSortBy] = useState('severity');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        ateSwVersion: '',
        serialNumbers: '',
        minSeverity: 'low',
    });

    const fetchAlarms = async () => {
        setLoading(true);
        setError('');
        try {
            const params = Object.fromEntries(
                Object.entries(filters).filter(([, value]) => String(value || '').trim() !== '')
            );
            const response = await axios.get(`${API_BASE}/root-cause-alarms`, { params });
            setAlarms(response.data.alarms || []);
            setSummary(response.data.summary || null);
        } catch (requestError) {
            setError(requestError.response?.data?.message || 'Failed to load root cause alarms.');
            setAlarms([]);
            setSummary(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAlarms();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sortedAlarms = useMemo(() => {
        return [...alarms].sort((a, b) => {
            if (sortBy === 'failRate') return b.failRate - a.failRate;
            if (sortBy === 'recent') return b.recentFailRate - a.recentFailRate;
            if (sortBy === 'step') return String(a.stepNumber).localeCompare(String(b.stepNumber), undefined, { numeric: true });
            return (
                severityRank[b.severityLevel] - severityRank[a.severityLevel] ||
                b.severityScore - a.severityScore ||
                b.failSessions - a.failSessions
            );
        });
    }, [alarms, sortBy]);

    const updateFilter = (key, value) => {
        setFilters((current) => ({ ...current, [key]: value }));
    };

    const resetFilters = () => {
        setFilters({
            startDate: '',
            endDate: '',
            ateSwVersion: '',
            serialNumbers: '',
            minSeverity: 'low',
        });
    };

    return (
        <div className={styles.container}>
            <GoToDashboardButton />

            <header className={styles.pageHeader}>
                <div>
                    <p className={styles.eyebrow}>Failure intelligence</p>
                    <h1 className={styles.title}>Root Cause Alarms</h1>
                    <p className={styles.description}>
                        Ranked alarms from failed steps, limit behavior, recent trend, affected serials, and co-failing evidence.
                    </p>
                </div>
            </header>

            <section className={styles.filterPanel}>
                <div className={styles.filterItem}>
                    <label>Start Date</label>
                    <input
                        type="date"
                        value={filters.startDate}
                        onChange={(event) => updateFilter('startDate', event.target.value)}
                    />
                </div>
                <div className={styles.filterItem}>
                    <label>End Date</label>
                    <input
                        type="date"
                        value={filters.endDate}
                        onChange={(event) => updateFilter('endDate', event.target.value)}
                    />
                </div>
                <div className={styles.filterItem}>
                    <label>ATE SW Version</label>
                    <input
                        type="text"
                        placeholder="Example: R004"
                        value={filters.ateSwVersion}
                        onChange={(event) => updateFilter('ateSwVersion', event.target.value)}
                    />
                </div>
                <div className={styles.filterItem}>
                    <label>Serial Numbers</label>
                    <input
                        type="text"
                        placeholder="Comma separated"
                        value={filters.serialNumbers}
                        onChange={(event) => updateFilter('serialNumbers', event.target.value)}
                    />
                </div>
                <div className={styles.filterItem}>
                    <label>Minimum Severity</label>
                    <select
                        value={filters.minSeverity}
                        onChange={(event) => updateFilter('minSeverity', event.target.value)}
                    >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                    </select>
                </div>
                <div className={styles.filterActions}>
                    <button type="button" onClick={fetchAlarms} className={styles.primaryButton}>
                        Run Analysis
                    </button>
                    <button type="button" onClick={resetFilters} className={styles.secondaryButton}>
                        Reset
                    </button>
                </div>
            </section>

            {summary && (
                <section className={styles.metricGrid}>
                    <div className={styles.metricCard}>
                        <span>Total Sessions</span>
                        <strong>{summary.totalSessions}</strong>
                    </div>
                    <div className={styles.metricCard}>
                        <span>Failed Products</span>
                        <strong>{summary.failedProductSessions}</strong>
                    </div>
                    <div className={styles.metricCard}>
                        <span>Steps Analyzed</span>
                        <strong>{summary.totalStepsAnalyzed}</strong>
                    </div>
                    <div className={styles.metricCard}>
                        <span>Active Alarms</span>
                        <strong>{summary.alarmsReturned}</strong>
                    </div>
                </section>
            )}

            <section className={styles.toolbar}>
                <div>
                    <p className={styles.eyebrow}>Alarm Queue</p>
                    <h2>Evidence-ranked failures</h2>
                </div>
                <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                    <option value="severity">Sort by severity</option>
                    <option value="failRate">Sort by fail rate</option>
                    <option value="recent">Sort by recent trend</option>
                    <option value="step">Sort by step number</option>
                </select>
            </section>

            {loading ? (
                <div className={styles.loadingScreen}>
                    <div className={styles.spinner}></div>
                    <p>Analyzing failures...</p>
                </div>
            ) : error ? (
                <div className={styles.emptyState}>{error}</div>
            ) : sortedAlarms.length === 0 ? (
                <div className={styles.emptyState}>No alarms matched the selected filters.</div>
            ) : (
                <div className={styles.alarmGrid}>
                    {sortedAlarms.map((alarm) => (
                        <article key={alarm.id} className={styles.alarmCard}>
                            <div className={styles.alarmHeader}>
                                <div>
                                    <span className={`${styles.badge} ${styles[alarm.severityLevel]}`}>
                                        {alarm.severityLevel}
                                    </span>
                                    <h3>{alarm.stepNumber} - {alarm.stepName}</h3>
                                </div>
                                <strong className={styles.score}>{alarm.severityScore}</strong>
                            </div>

                            <div className={styles.alarmStats}>
                                <div>
                                    <span>Fail rate</span>
                                    <strong>{formatPercent(alarm.failRate)}</strong>
                                </div>
                                <div>
                                    <span>Failures</span>
                                    <strong>{alarm.failSessions}/{alarm.observedSessions}</strong>
                                </div>
                                <div>
                                    <span>Recent</span>
                                    <strong>{formatPercent(alarm.recentFailRate)}</strong>
                                </div>
                                <div>
                                    <span>Near/Outside</span>
                                    <strong>{alarm.nearLimitSessions}/{alarm.outsideLimitSessions}</strong>
                                </div>
                            </div>

                            <ul className={styles.evidenceList}>
                                {alarm.evidence.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>

                            <div className={styles.chips}>
                                {alarm.affectedSerials.map((serial) => (
                                    <span key={serial}>SN {serial}</span>
                                ))}
                                {alarm.ateSwVersions.map((version) => (
                                    <span key={version}>{version}</span>
                                ))}
                            </div>

                            <button
                                type="button"
                                onClick={() => setSelectedAlarm(alarm)}
                                className={styles.primaryButton}
                            >
                                Investigate
                            </button>
                        </article>
                    ))}
                </div>
            )}

            <Modal
                isOpen={Boolean(selectedAlarm)}
                onRequestClose={() => setSelectedAlarm(null)}
                className={styles.modalContent}
                overlayClassName={styles.modalOverlay}
            >
                {selectedAlarm && (
                    <div>
                        <button onClick={() => setSelectedAlarm(null)} className={styles.closeModalButton}>
                            Close
                        </button>
                        <p className={styles.eyebrow}>Investigation Plan</p>
                        <h2>{selectedAlarm.stepNumber} - {selectedAlarm.stepName}</h2>
                        <p className={styles.recommendation}>{selectedAlarm.recommendation}</p>

                        <h3>Co-failing Steps</h3>
                        {selectedAlarm.relatedSteps.length ? (
                            <table className={styles.detailTable}>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Step</th>
                                        <th>Name</th>
                                        <th>Count</th>
                                        <th>When Primary Fails</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedAlarm.relatedSteps.map((step, index) => (
                                        <tr key={step.stepNumber}>
                                            <td>{index + 1}</td>
                                            <td>{step.stepNumber}</td>
                                            <td>{step.stepName || 'Unnamed step'}</td>
                                            <td>{step.count}</td>
                                            <td>{formatPercent(step.probabilityWhenPrimaryFails)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p className={styles.muted}>No repeated co-failing step was found.</p>
                        )}

                        <h3>Example Evidence</h3>
                        <table className={styles.detailTable}>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Serial</th>
                                    <th>File</th>
                                    <th>Started</th>
                                    <th>Value</th>
                                    <th>Limits</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedAlarm.examples.map((example, index) => (
                                    <tr key={`${example.serialNumber}-${example.filename}`}>
                                        <td>{index + 1}</td>
                                        <td>{example.serialNumber}</td>
                                        <td>{example.filename}</td>
                                        <td>{example.testStarted}</td>
                                        <td>{example.measureValue ?? 'N/A'}</td>
                                        <td>{example.limitLow ?? 'N/A'} / {example.limitHigh ?? 'N/A'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default RootCauseAlarms;
